// components/StreamChat.tsx
import * as React from 'react';
import { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
	Alert,
	Box,
	Snackbar,
	Stack,
	Divider,
	Button,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Typography,
	CircularProgress,
	IconButton,
	TextField,
	Tooltip,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { useFetchCompanyQuery } from '../../../services/companyApi';
import { useGetFinancialPlanQuery } from '../../../services/financialPlanApi';
import { useFetchBusinessPlanQuery } from '../../../services/businessPlan';
import { RootState } from '../../../store/store';
import { copyHtmlContent, insertHtmlContent, getSelection } from '../../../utils/wordUtils';
import { CustomerTreeViewPopper } from '../graph/components/CustomerTreeView/CustomerTreeViewPopper';
import { Instrument } from '../../../types/instrument';
import { selectInstrument, removeInstrument } from '../instrumentSearch/slice';
import type { AppDispatch } from '../../../store/store';
import { ChatMessage as ChatMessageComponent } from './components/ChatMessage';
import { DataInclusionControls } from './components/DataInclusionControls';
import { SelectedItemsList } from './components/SelectedItemsList';
import { ChatInput } from './components/ChatInput';
import { ChatContainer } from './components/ChatContainer';
import { Message } from './components/ChatMessage/types';
import { ActiveCompanyTreeView } from '../graph/components/CustomerTreeView/ActiveCompanyTreeView';
import TaskPicker from './components/TaskPicker/TaskPicker';
import { ExtendedProjectTask } from './components/TaskPicker/types';
import { ItemType } from './components/SelectedItemsList/types';
import InstrumentSearch from '../instrumentSearch/InstrumentSearch';
import { CompanyNotesPopper } from './components/CompanyNotes/CompanyNotesPopper';
import { useFetchScenarioQuery } from '../../../services/instrumentsApi';
import ProjectSelector from './components/Projects';
import { Project } from '../../../types/project';
import { useIntl } from 'react-intl';
import { ContentCopy } from '@mui/icons-material';
import {
	ChatMessage,
	useDeleteGrantedAgentChatHistoryMutation,
	useGetGrantedAgentChatHistoryArrayQuery,
	useGetGrantedAgentChatHistoryQuery,
	useCreateGrantedAgentConversationMutation,
	useUpdateConversationTitleMutation,
	aiApi,
	TAGS,
} from '../../../services/aiAgentApi';
import { getUserId } from '../../../utils/userUtils';
import { useSSEStream } from '../../../hooks/useSSEStream';
import { mockStreamingScenarios, simulateStreamingResponse } from './mockData';
import { ConversationSidebar } from './components/ConversationSidebar';

export function StreamChat() {
	const { formatMessage } = useIntl();
	const [messages, setMessages] = useState<Message[]>([]);
	const [inputValue, setInputValue] = useState('');
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [autoInsert, setAutoInsert] = useState(false);
	const [selectedTemplate, setSelectedTemplate] = useState('no-template');
	// Auto-load history: no dialog/pending state
	const [askedConversations, setAskedConversations] = useState<Set<string>>(new Set());
	const [includedDataTypes, setIncludedDataTypes] = useState<
		Array<'financialPlan' | 'businessPlan' | 'company' | 'scenario' | 'template'>
	>([]);
	const [selectedFiles, setSelectedFiles] = useState<Array<{ id: string; name: string; webUrl: string }>>([]);
	const [selectedTasks, setSelectedTasks] = useState<Array<{ id: string; name: string; content: any }>>([]);
	const [selectedNotes, setSelectedNotes] = useState<Array<{ id: string; name: string; content: any }>>([]);
	const [selectedProjects, setSelectedProjects] = useState<Array<{ id: string; name: string; content: Project }>>([]);
	const [selectedContent, setSelectedContent] = useState<Array<{ id: string; name: string; content: string }>>([]);
	const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
	const [deleteChat] = useDeleteGrantedAgentChatHistoryMutation();
	const [createConversation] = useCreateGrantedAgentConversationMutation();
	const [updateConversationTitle, { isLoading: isUpdatingTitle }] = useUpdateConversationTitleMutation();
	const [isEditingTitle, setIsEditingTitle] = useState(false);
	const [editingTitleValue, setEditingTitleValue] = useState('');
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

	const dispatch = useDispatch<AppDispatch>();
	const selectedInstruments = useSelector((state: RootState) => state.instrumentSearch as Instrument[]);
	const selectedCompany = useSelector((state: RootState) => state.companySearch.selected);
	const chatId = `word-document-${selectedCompany?.companyId || 'no-company'}`;

	const { data: company } = useFetchCompanyQuery({ companyId: selectedCompany?.companyId }, { skip: !selectedCompany?.companyId });

	const { data: financialPlan } = useGetFinancialPlanQuery(
		{ companyId: selectedCompany?.companyId },
		{ skip: !selectedCompany?.companyId },
	);
	const { data: businessPlan } = useFetchBusinessPlanQuery(
		{ companyId: selectedCompany?.companyId },
		{ skip: !selectedCompany?.companyId },
	);
	const { data: scenario } = useFetchScenarioQuery(
		{ companyId: selectedCompany?.companyId, businessId: selectedCompany?.businessId },
		{ skip: !selectedCompany?.companyId || !selectedCompany?.businessId },
	);

	const userId = getUserId();

	const [selectedConversationId, setSelectedConversationId] = React.useState<string | undefined>(undefined);

	// Get agent chat history array
	const {
		data: agentHistoryArray,
		isFetching: agentHistoryArrayIsFetching,
		isLoading: agentHistoryArrayIsLoading,
	} = useGetGrantedAgentChatHistoryArrayQuery(
		{
			userId: userId,
		},
		{
			skip: !userId,
		},
	);

	// Get specific agent conversation history
	const {
		data: agentConversationHistory,
		isFetching: agentConversationHistoryIsFetching,
		isLoading: agentConversationHistoryIsLoading,
	} = useGetGrantedAgentChatHistoryQuery(
		{
			userId: userId,
			conversationId: selectedConversationId,
		},
		{
			skip: !userId || !selectedConversationId,
		},
	);

	const chatContainerRef = useRef<HTMLDivElement>(null);
	const prevStreamLoadingRef = useRef<boolean>(false);

	// Use streaming hook
	const {
		startStream,
		stopStream,
		cancelStream,
		isLoading: streamLoading,
		streamedText,
		statusMessage,
		isToolUsage: streamIsToolUsage,
		error: streamError,
		conversationId: streamConversationId,
	} = useSSEStream();
	const [activeStreamingMessageId, setActiveStreamingMessageId] = useState<string | null>(null);

	useEffect(() => {
		if (chatContainerRef.current) {
			chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
		}
	}, [messages]);

	// Auto-load conversation history when available
	useEffect(() => {
		if (agentConversationHistory?.messages && agentConversationHistory?.messages?.length > 0 && selectedConversationId) {
			const formattedHistory = agentConversationHistory?.messages.map(msg => ({
				content: msg.content,
				isUser: msg.role?.toLowerCase() === 'user',
				type: msg.type || null,
				name: msg.name || null,
				file_data: msg.file_data || null,
				webUrl: msg.webUrl || null,
				id: msg.id || null,
			}));

			setMessages(formattedHistory as Message[]);
		} else if (selectedConversationId && (!agentConversationHistory?.messages || agentConversationHistory.messages.length === 0)) {
			// Clear messages if conversation has no history
			setMessages([]);
		}
	}, [selectedConversationId, agentConversationHistory]);

	// Effect to automatically select the first conversation from history array
	useEffect(() => {
		if (agentHistoryArray?.history && agentHistoryArray.history.length > 0 && !selectedConversationId) {
			// Get the first conversation (most recent by updatedAt)
			const sortedConversations = [...agentHistoryArray.history].sort(
				(a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
			);
			const firstConversation = sortedConversations[0];
			setSelectedConversationId(firstConversation.conversationId);
		}
	}, [agentHistoryArray, selectedConversationId]);

	// Effect to update selectedConversationId when stream returns a new conversationId
	useEffect(() => {
		if (streamConversationId && streamConversationId !== selectedConversationId) {
			setSelectedConversationId(streamConversationId);
		}
	}, [streamConversationId]);

	// Effect to invalidate cache when streaming completes
	useEffect(() => {
		const wasStreaming = prevStreamLoadingRef.current;
		const isNowComplete = !streamLoading;

		// If we just finished streaming (was true, now false), invalidate the cache
		if (wasStreaming && isNowComplete && selectedConversationId) {
			dispatch(
				aiApi.util.invalidateTags([
					{ type: TAGS.CHAT, id: `history-${userId}-${selectedConversationId}` },
					{ type: TAGS.CHAT, id: `history-array-${userId}` },
				]),
			);
		}

		// Update the ref for next render
		prevStreamLoadingRef.current = streamLoading;
	}, [streamLoading, selectedConversationId, userId, dispatch]);

	// Function to handle conversation change
	const handleConversationChange = (conversationId: string) => {
		setSelectedConversationId(conversationId);
		// Clear the askedConversations to allow loading the new conversation
		setAskedConversations(new Set());
		setMessages([]);
	};

	// Function to handle creating a new conversation
	const handleNewConversation = async () => {
		try {
			const result = await createConversation({ userId }).unwrap();
			if (result?.conversationId) {
				setSelectedConversationId(result.conversationId);
				setMessages([]);
				setAskedConversations(new Set());
			}
		} catch (error) {
			console.error('Error creating new conversation:', error);
			setError('Failed to create new conversation');
		}
	};

	// Removed history dialog handlers

	const handleDeleteChat = async () => {
		try {
			if (!selectedConversationId || !userId) {
				console.error('No conversation ID or user ID selected');
				return;
			}

			await deleteChat({
				userId: userId,
				conversationId: selectedConversationId,
			}).unwrap();
			setMessages([]);
			setSelectedConversationId(undefined);
			setAskedConversations(new Set());
			setDeleteDialogOpen(false);
		} catch (error: any) {
			console.error('Error deleting chat:', {
				error,
				status: error?.status,
				originalStatus: error?.originalStatus,
				data: error?.data,
				errorText: error?.error,
			});
			setError('Failed to delete chat history');
			setDeleteDialogOpen(false);
		}
	};

	// Get current conversation title
	const currentConversation = agentHistoryArray?.history?.find(c => c.conversationId === selectedConversationId);
	const currentTitle = currentConversation?.title || '';

	const handleStartEditTitle = () => {
		setEditingTitleValue(currentTitle);
		setIsEditingTitle(true);
	};

	const handleCancelEditTitle = () => {
		setIsEditingTitle(false);
		setEditingTitleValue('');
	};

	const handleSaveTitle = async () => {
		if (!selectedConversationId || !editingTitleValue.trim()) {
			handleCancelEditTitle();
			return;
		}
		try {
			await updateConversationTitle({
				userId: userId,
				conversationId: selectedConversationId,
				title: editingTitleValue.trim(),
			}).unwrap();
			setIsEditingTitle(false);
			setEditingTitleValue('');
		} catch (error) {
			console.error('Error updating conversation title:', error);
			setError('Failed to update conversation title');
		}
	};

	const handleTitleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			handleSaveTitle();
		} else if (e.key === 'Escape') {
			handleCancelEditTitle();
		}
	};

	const handleSend = async (model: string, provider: string) => {
		if (!inputValue.trim()) return;

		const userMessage: Message = {
			content: inputValue,
			isUser: true,
		};

		setMessages(prev => [...prev, userMessage]);
		const currentInput = inputValue;
		setInputValue('');
		setLoading(true);
		setError(null);

		try {
			// Build the message array for streaming, same as in AgentChat
			const apiMessages: any[] = [];

			if (includedDataTypes.includes('template')) {
				const content = await copyHtmlContent();
				apiMessages.push({
					role: 'user',
					content: `template: ${JSON.stringify(content)}`,
				});
			}

			if (includedDataTypes.includes('company') && (company ?? selectedCompany)) {
				apiMessages.push({
					role: 'user',
					content: `company: ${JSON.stringify(company ?? selectedCompany)}`,
				});
			}

			if (includedDataTypes.includes('financialPlan') && financialPlan) {
				apiMessages.push({
					role: 'user',
					content: `financialPlan: ${JSON.stringify(financialPlan)}`,
				});
			}

			if (includedDataTypes.includes('businessPlan') && businessPlan) {
				apiMessages.push({
					role: 'user',
					content: `businessPlan: ${JSON.stringify(businessPlan)}`,
				});
			}

			if (includedDataTypes.includes('scenario') && scenario) {
				apiMessages.push({
					role: 'user',
					content: `scenario: ${JSON.stringify(scenario)}`,
				});
			}

			if (selectedInstruments.length > 0) {
				selectedInstruments.forEach(instrument => {
					apiMessages.push({
						role: 'user',
						content: `selectedInstrument: ${JSON.stringify(instrument)}`,
					});
				});
			}

			if (selectedNotes.length > 0) {
				selectedNotes.forEach(note => {
					apiMessages.push({
						role: 'user',
						content: `selectedNote: ${JSON.stringify(note.content)}`,
					});
				});
			}

			if (selectedContent.length > 0) {
				selectedContent.forEach(content => {
					apiMessages.push({
						role: 'user',
						content: `selectedContent: ${JSON.stringify(content.content)}`,
					});
				});
			}

			if (selectedTasks.length > 0) {
				selectedTasks.forEach(task => {
					apiMessages.push({
						role: 'user',
						content: `selectedTask: ${JSON.stringify(task.content)}`,
					});
				});
			}

			if (selectedFiles.length > 0) {
				selectedFiles.forEach(file => {
					apiMessages.push({
						role: 'user',
						id: file.id,
						name: file.name,
						type: 'file',
						webUrl: file.webUrl,
						content: '',
					});
				});
			}

			if (selectedProjects.length > 0) {
				selectedProjects.forEach(project => {
					apiMessages.push({
						role: 'user',
						content: `selectedProject: ${JSON.stringify(project.content)}`,
					});
				});
			}

			// Add the actual user message
			apiMessages.push({
				role: 'user',
				content: currentInput,
			});

			// Add a placeholder message that will be updated with the stream
			const streamingMessageId = `streaming-${Date.now()}`;
			const aiMessage: Message = {
				content: 'Käsittelen pyyntöäsi...',
				isUser: false,
				id: streamingMessageId,
				isStreaming: true,
			};
			setMessages(prev => [...prev, aiMessage]);
			setActiveStreamingMessageId(streamingMessageId);

			// Start the stream with current conversationId
			// conversationId will be updated via the useEffect watching streamConversationId
			await startStream(apiMessages, model, provider, selectedConversationId);
		} catch (error: any) {
			const errorMessage = error?.message || error?.data?.message || JSON.stringify(error);
			const errorText =
				formatMessage({ id: 'error.aiError' }, { message: errorMessage || 'Unknown error' }) || `AI Error: ${errorMessage}`;
			setError(errorText);
			console.error('AI Chat error:', error);

			// Update the active streaming message with error content
			if (activeStreamingMessageId) {
				setMessages(prev => {
					const newMessages = [...prev];
					const messageIndex = newMessages.findIndex(msg => msg.id === activeStreamingMessageId);
					if (messageIndex !== -1) {
						newMessages[messageIndex].content =
							formatMessage({ id: 'error.aiErrorMessage' }, { message: errorMessage || 'Unknown error' }) ||
							`Sorry, I encountered an error: ${errorMessage}`;
					}
					return newMessages;
				});
				setActiveStreamingMessageId(null);
			}
		} finally {
			setLoading(false);
		}
	};

	// Update the active streaming message with streaming content or status
	useEffect(() => {
		if (activeStreamingMessageId && (streamedText || statusMessage)) {
			setMessages(prev => {
				const newMessages = [...prev];
				const messageIndex = newMessages.findIndex(msg => msg.id === activeStreamingMessageId);
				if (messageIndex !== -1) {
					// Always show the streamed text as main content, or default message if no text yet
					newMessages[messageIndex].content = streamedText || 'Käsittelen pyyntöäsi...';
					newMessages[messageIndex].isStreaming = streamLoading;

					// Show status message separately if it exists and we don't have much content yet
					// Hide status when we have actual content flowing
					const hasActualContent = streamedText && streamedText.length > 50;

					if (statusMessage && !hasActualContent) {
						newMessages[messageIndex].statusMessage = statusMessage;
					} else {
						newMessages[messageIndex].statusMessage = undefined;
					}

					// Use tool usage state from the hook (based on actual 'toolusage' chunk type)
					// But only if we don't have actual content yet
					newMessages[messageIndex].isToolUsage = !hasActualContent && streamIsToolUsage;
				}
				return newMessages;
			});
		}
	}, [streamedText, statusMessage, activeStreamingMessageId, streamLoading, streamIsToolUsage]);

	// Handle auto-insert when streaming is complete
	useEffect(() => {
		if (!streamLoading && streamedText && autoInsert) {
			insertHtmlContent(streamedText).catch(error => {
				setError(`Error inserting content: ${error.message}`);
			});
		}
		// Clear active streaming message ID and update final message state when streaming is complete
		if (!streamLoading && activeStreamingMessageId) {
			setMessages(prev => {
				const newMessages = [...prev];
				const messageIndex = newMessages.findIndex(msg => msg.id === activeStreamingMessageId);
				if (messageIndex !== -1) {
					newMessages[messageIndex].isStreaming = false;
					newMessages[messageIndex].statusMessage = undefined;
				}
				return newMessages;
			});
			setActiveStreamingMessageId(null);
		}
	}, [streamLoading, streamedText, autoInsert, activeStreamingMessageId]);

	const handleManualInsert = async (content: string) => {
		try {
			await insertHtmlContent(content);
		} catch (error: any) {
			setError(`Error inserting content: ${error.message}`);
		}
	};

	const handleCustomerSelect = (_customerName: string) => {
		//TODO: Handle the selected customer
	};

	const handleFileSelect = (file: any) => {
		const fileMessage: Message = {
			role: 'user',
			id: file.id,
			name: file.name,
			type: 'file',
			webUrl: file.webUrl,
			content: '',
			isUser: true,
		};
		setMessages(prev => [...prev, fileMessage]);
		setSelectedFiles(prev => [...prev, { id: file.id, name: file.name, webUrl: file.webUrl }]);
	};

	const handleRemoveFile = (fileId: string) => {
		setSelectedFiles(prev => prev.filter(file => file.id !== fileId));
		setMessages(prev => prev.filter(msg => msg.id !== fileId));
	};

	const handleInstrumentSelect = async (instrument: Instrument) => {
		await dispatch(selectInstrument(instrument));
		const instrumentMessage: Message = {
			role: 'user',
			content: `Selected instrument: ${JSON.stringify(instrument)}`,
			isUser: true,
		};
		setMessages(prev => [...prev, instrumentMessage]);
	};

	const handleRemoveInstrument = async (instrumentId: string) => {
		const instrument = selectedInstruments.find(i => i.id === instrumentId);
		if (instrument) {
			await dispatch(removeInstrument(instrument));
			setMessages(prev => prev.filter(msg => msg.content && !msg.content.includes(instrumentId)));
		}
	};

	const handleDataInclusionToggle = (type: 'financialPlan' | 'businessPlan' | 'company' | 'scenario' | 'template') => {
		setIncludedDataTypes(prev => (prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]));
	};

	const addSelectedTask = (task: ExtendedProjectTask | undefined) => {
		if (task?.projectTaskId && task?.projectTaskName) {
			setSelectedTasks(prev => [
				...prev,
				{
					id: task.projectTaskId!,
					name: task.projectTaskName!,
					content: task,
				},
			]);
		}
	};

	const handleRemoveTask = (taskId: string) => {
		setSelectedTasks(prev => prev.filter(task => task.id !== taskId));
		setMessages(prev => prev.filter(msg => msg.id !== taskId));
	};

	const handleNoteSelect = (note: any) => {
		setSelectedNotes(prev => [
			...prev,
			{
				id: note.companyNotesId,
				name: note.title,
				content: note,
			},
		]);
	};

	const handleRemoveNote = (noteId: string) => {
		setSelectedNotes(prev => prev.filter(note => note.id !== noteId));
		setMessages(prev => prev.filter(msg => msg.id !== noteId));
	};

	const addSelectedProject = (project: Project | undefined) => {
		if (project?.projectId && project?.projectName) {
			setSelectedProjects(prev => [
				...prev,
				{
					id: project.projectId!,
					name: project.projectName,
					content: project,
				},
			]);
		}
	};

	const handleClearAll = async () => {
		try {
			// Delete chat history from server
			if (selectedConversationId) {
				await deleteChat({
					userId: userId,
					conversationId: selectedConversationId,
				}).unwrap();
			}

			// Clear local state
			setMessages([]);
			setSelectedFiles([]);
			setSelectedTasks([]);
			setSelectedNotes([]);
			setSelectedProjects([]);
			setInputValue('');
			setIncludedDataTypes([]);
			setSelectedTemplate('no-template');
			setSelectedConversationId(undefined);
			setAskedConversations(new Set());
			// Clear selected instruments from Redux store
			selectedInstruments.forEach(instrument => {
				dispatch(removeInstrument(instrument));
			});
		} catch (error: any) {
			console.error('Error clearing chat:', {
				error,
				status: error?.status,
				originalStatus: error?.originalStatus,
				data: error?.data,
				errorText: error?.error,
			});
			setError('Failed to clear chat history');
		}
	};

	const handleRemoveMessage = (index: number) => {
		setMessages(prev => prev.filter((_, i) => i !== index));
	};

	const handleCopySelection = async () => {
		try {
			const content = await getSelection();
			if (content) {
				const id = `selection-${Date.now()}`;
				setSelectedContent(prev => [
					...prev,
					{
						id,
						name: 'Selected Content',
						content,
					},
				]);
			}
		} catch (error) {
			console.error('Error copying selection:', error);
			setError('Failed to copy selection');
		}
	};

	const handleRemoveSelection = (id: string) => {
		setSelectedContent(prev => prev.filter(item => item.id !== id));
	};

	const handleStop = async () => {
		await cancelStream();
		setLoading(false);
		setActiveStreamingMessageId(null);
	};

	const handleMockStreaming = async (scenarioId: string) => {
		const scenario = mockStreamingScenarios.find(s => s.id === scenarioId);
		if (!scenario) return;

		// Add user message
		const userMessage: Message = {
			content: `Testing scenario: ${scenario.name}`,
			isUser: true,
		};
		setMessages(prev => [...prev, userMessage]);

		// Add streaming message
		const streamingMessageId = `mock-streaming-${Date.now()}`;
		const aiMessage: Message = {
			content: 'Starting mock streaming...',
			isUser: false,
			id: streamingMessageId,
			isStreaming: true,
		};
		setMessages(prev => [...prev, aiMessage]);
		setActiveStreamingMessageId(streamingMessageId);
		setLoading(true);

		// Simulate streaming
		await simulateStreamingResponse(scenario, (content, statusMessage, isToolUsage, isComplete) => {
			setMessages(prev => {
				const newMessages = [...prev];
				const messageIndex = newMessages.findIndex(msg => msg.id === streamingMessageId);
				if (messageIndex !== -1) {
					newMessages[messageIndex].content = content;
					newMessages[messageIndex].statusMessage = statusMessage;
					newMessages[messageIndex].isToolUsage = isToolUsage || false;
					newMessages[messageIndex].isStreaming = !isComplete;
				}
				return newMessages;
			});

			if (isComplete) {
				setLoading(false);
				setActiveStreamingMessageId(null);
			}
		});
	};

	return (
		<Box
			sx={{
				bgcolor: 'background.paper',
				height: '100%',
				display: 'flex',
				flexDirection: 'row',
				overflow: 'hidden',
			}}
		>
			{/* Conversation Sidebar with collapse */}
			<ConversationSidebar
				conversations={agentHistoryArray?.history || []}
				selectedConversationId={selectedConversationId}
				onConversationSelect={handleConversationChange}
				onNewConversation={handleNewConversation}
				onDeleteConversation={conversationId => {
					deleteChat({ userId, conversationId });
					if (conversationId === selectedConversationId) {
						setSelectedConversationId(undefined);
						setMessages([]);
						setAskedConversations(new Set());
					}
				}}
				loading={loading || streamLoading}
				isCollapsed={isSidebarCollapsed}
				onToggleCollapse={() => setIsSidebarCollapsed(prev => !prev)}
			/>

			{/* Main Chat Area */}
			<Box
				sx={{
					flex: 1,
					p: 2,
					gap: 2,
					display: 'flex',
					flexDirection: 'column',
					overflow: 'hidden',
					ml: isSidebarCollapsed ? 0 : 0,
				}}
			>
				{/* Sidebar toggle header with conversation title */}
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						mb: 1,
						borderBottom: '1px solid',
						borderColor: 'divider',
						pb: 1,
					}}
				>
					{/* <IconButton size='small' onClick={() => setIsSidebarCollapsed(prev => !prev)} sx={{ mr: 1 }}>
						{isSidebarCollapsed ? <MenuOpenIcon /> : <ChevronLeftIcon />}
					</IconButton> */}

					{isEditingTitle ? (
						<Box sx={{ display: 'flex', alignItems: 'center', flex: 1, gap: 0.5 }}>
							<TextField
								value={editingTitleValue}
								onChange={e => setEditingTitleValue(e.target.value)}
								onKeyDown={handleTitleKeyDown}
								size='small'
								autoFocus
								fullWidth
								disabled={isUpdatingTitle}
								placeholder={formatMessage(
									{ id: 'chat.titlePlaceholder' },
									{ defaultMessage: 'Enter conversation title...' },
								)}
								sx={{
									'& .MuiInputBase-input': {
										py: 0.5,
										px: 1,
										fontSize: '0.875rem',
									},
								}}
							/>
							<IconButton size='small' onClick={handleSaveTitle} disabled={isUpdatingTitle || !editingTitleValue.trim()}>
								<CheckIcon fontSize='small' color={editingTitleValue.trim() ? 'success' : 'disabled'} />
							</IconButton>
							<IconButton size='small' onClick={handleCancelEditTitle} disabled={isUpdatingTitle}>
								<CloseIcon fontSize='small' color='error' />
							</IconButton>
						</Box>
					) : (
						<>
							<Tooltip
								title={
									selectedConversationId
										? formatMessage({ id: 'chat.editTitle' }, { defaultMessage: 'Click to edit title' })
										: ''
								}
							>
								<Typography
									variant='subtitle2'
									onClick={selectedConversationId ? handleStartEditTitle : undefined}
									sx={{
										flex: 1,
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										whiteSpace: 'nowrap',
										cursor: selectedConversationId ? 'pointer' : 'default',
										'&:hover': selectedConversationId
											? {
													bgcolor: 'action.hover',
													borderRadius: 1,
												}
											: {},
										px: 1,
										py: 0.5,
									}}
								>
									{currentTitle ||
										formatMessage({ id: 'chat.untitledConversation' }, { defaultMessage: 'Untitled conversation' })}
								</Typography>
							</Tooltip>
							{selectedConversationId && (
								<Tooltip
									title={formatMessage(
										{ id: 'chat.deleteConversation.title' },
										{ defaultMessage: 'Delete conversation' },
									)}
								>
									<IconButton size='small' onClick={() => setDeleteDialogOpen(true)} sx={{ ml: 0.5 }} color='error'>
										<DeleteIcon fontSize='small' />
									</IconButton>
								</Tooltip>
							)}
						</>
					)}
				</Box>

				{/* Debug information */}
				{/* {process.env.NODE_ENV === 'development' && (
				<Box sx={{ p: 1, bgcolor: 'grey.100', fontSize: '12px', mb: 1 }}>
					<div>Current Conversation ID: {selectedConversationId || 'None'}</div>
					<div>History Array Length: {agentHistoryArray?.history?.length || 0}</div>
					<div>Conversation History Messages: {agentConversationHistory?.messages?.length || 0}</div>
					{agentHistoryArray?.history && agentHistoryArray.history.length > 0 && (
						<div>First Conversation: {agentHistoryArray.history[0].conversationId}</div>
					)}
				</Box>
			)} */}

				{/* Mock Streaming Panel - Development Only */}
				{process.env.NODE_ENV === 'development' && (
					<Accordion sx={{ mb: 2 }}>
						<AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls='mock-streaming-content' id='mock-streaming-header'>
							<Typography variant='h6' color='primary'>
								🧪 Mock Streaming Test Panel
							</Typography>
						</AccordionSummary>
						<AccordionDetails>
							<Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
								Test the enhanced streaming UI with different scenarios:
							</Typography>
							<Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
								{mockStreamingScenarios.map(scenario => (
									<Button
										key={scenario.id}
										variant='outlined'
										size='small'
										onClick={() => handleMockStreaming(scenario.id)}
										disabled={loading || streamLoading}
										sx={{ mb: 1 }}
									>
										{scenario.name}
									</Button>
								))}
							</Stack>
						</AccordionDetails>
					</Accordion>
				)}

				<ChatContainer ref={chatContainerRef}>
					{agentConversationHistoryIsLoading || agentConversationHistoryIsFetching ? (
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								height: '100%',
								flexDirection: 'column',
								gap: 2,
							}}
						>
							<CircularProgress />
							<Typography variant='body2' color='text.secondary'>
								{formatMessage({ id: 'chat.loadingConversation' }, { defaultMessage: 'Loading conversation...' })}
							</Typography>
						</Box>
					) : (
						messages
							.filter(message => !message.type)
							.map((message, index) => (
								<ChatMessageComponent
									key={index}
									message={message}
									onManualInsert={handleManualInsert}
									onRemove={() => handleRemoveMessage(index)}
								/>
							))
					)}
				</ChatContainer>

				<Stack
					direction='column'
					spacing={1}
					sx={{
						flexShrink: 0,
						position: 'sticky',
						bottom: 0,
						backgroundColor: 'background.paper',
						pt: 1,
						borderTop: '1px solid',
						borderColor: 'divider',
					}}
				>
					<Stack direction='row' spacing={0.5} alignItems='center'>
						<DataInclusionControls
							selectedCompanyId={selectedCompany?.companyId}
							includeFinancialPlan={includedDataTypes.includes('financialPlan')}
							includeBusinessPlan={includedDataTypes.includes('businessPlan')}
							includeCompanyData={includedDataTypes.includes('company')}
							includeScenario={includedDataTypes.includes('scenario')}
							includeTemplate={includedDataTypes.includes('template')}
							onToggle={handleDataInclusionToggle}
							hasFinancialPlan={!!financialPlan?.rowKey}
							hasBusinessPlan={!!businessPlan && businessPlan.author !== 'GENERATED'}
							hasCompanyData={!!company}
							hasScenario={!!scenario}
							hasTemplate={!!selectedTemplate}
						/>
						<Button variant='outlined' size='small' startIcon={<ContentCopy />} onClick={handleCopySelection}>
							{formatMessage({ id: 'common.copySelection' }, { defaultMessage: 'Copy Selection' })}
						</Button>
					</Stack>

					<Stack direction='row' spacing={0.5} alignItems='center' justifyContent='space-between'>
						<Stack direction='row' spacing={0.5} alignItems='center'>
							<TaskPicker selectTask={addSelectedTask} />
							<ActiveCompanyTreeView customerName={selectedCompany?.name} onFileSelect={handleFileSelect} />
							<CustomerTreeViewPopper onCustomerSelect={handleCustomerSelect} onFileSelect={handleFileSelect} />
							<Divider orientation='vertical' flexItem />
							<ProjectSelector selectProject={addSelectedProject} companyId={selectedCompany?.companyId} />
							<CompanyNotesPopper onNoteSelect={handleNoteSelect} />
						</Stack>
						<Box sx={{ minWidth: '20rem' }}>
							<InstrumentSearch />
						</Box>
					</Stack>

					<Stack direction='row' spacing={1}>
						{selectedInstruments.length > 0 && (
							<SelectedItemsList
								items={selectedInstruments.map(i => ({ id: i.id ?? '', name: i.name ?? '' }))}
								onRemove={handleRemoveInstrument}
								type={ItemType.INSTRUMENT}
							/>
						)}
						{selectedProjects.length > 0 && (
							<SelectedItemsList
								items={selectedProjects}
								onRemove={(id: string) => setSelectedProjects(prev => prev.filter(p => p.id !== id))}
								type={ItemType.PROJECT}
							/>
						)}
						{selectedFiles.length > 0 && (
							<SelectedItemsList items={selectedFiles} onRemove={handleRemoveFile} type={ItemType.FILE} />
						)}
						{selectedTasks.length > 0 && (
							<SelectedItemsList items={selectedTasks} onRemove={handleRemoveTask} type={ItemType.TASK} />
						)}
						{selectedNotes.length > 0 && (
							<SelectedItemsList items={selectedNotes} onRemove={handleRemoveNote} type={ItemType.NOTE} />
						)}
						{selectedContent.length > 0 && (
							<SelectedItemsList items={selectedContent} onRemove={handleRemoveSelection} type={ItemType.SELECTION} />
						)}
					</Stack>

					<ChatInput
						value={inputValue}
						onChange={setInputValue}
						onSend={handleSend}
						loading={loading || streamLoading}
						autoInsert={autoInsert}
						onAutoInsertChange={setAutoInsert}
						onClear={handleClearAll}
						lockedProvider={'openai'}
						setValue={setInputValue}
						onStop={handleStop}
					/>
				</Stack>

				{/* History dialog removed: history loads automatically */}

				<Snackbar
					open={!!error || !!streamError}
					autoHideDuration={6000}
					onClose={() => {
						setError(null);
					}}
				>
					<Alert
						onClose={() => {
							setError(null);
						}}
						severity='error'
						sx={{ width: '100%' }}
					>
						{error || streamError}
					</Alert>
				</Snackbar>

				{/* Delete conversation confirmation dialog */}
				<Dialog
					open={deleteDialogOpen}
					onClose={() => setDeleteDialogOpen(false)}
					aria-labelledby='delete-conversation-dialog-title'
					aria-describedby='delete-conversation-dialog-description'
				>
					<DialogTitle id='delete-conversation-dialog-title'>
						{formatMessage({ id: 'chat.deleteConversation.title' }, { defaultMessage: 'Delete conversation?' })}
					</DialogTitle>
					<DialogContent>
						<DialogContentText id='delete-conversation-dialog-description'>
							{formatMessage(
								{ id: 'chat.deleteConversation.message' },
								{ defaultMessage: 'Are you sure you want to delete this conversation? This action cannot be undone.' },
							)}
						</DialogContentText>
					</DialogContent>
					<DialogActions>
						<Button onClick={() => setDeleteDialogOpen(false)}>
							{formatMessage({ id: 'common.cancel' }, { defaultMessage: 'Cancel' })}
						</Button>
						<Button onClick={handleDeleteChat} color='error' variant='contained'>
							{formatMessage({ id: 'common.delete' }, { defaultMessage: 'Delete' })}
						</Button>
					</DialogActions>
				</Dialog>
			</Box>
		</Box>
	);
}

export default StreamChat;
